<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12 col-lg-4 mb-3 mb-lg-0">
        <b-card>
          <template v-slot:header>Ongoing Public Quizzes</template>
          <template v-slot:footer>
            <span class="text-small">Click a listed quiz to join in.</span>
          </template>
          <QuizItem
            v-for="quiz in sortedPublicQuizzes"
            v-bind:key="quiz.id"
            v-bind:quiz="quiz"
            @join-requested="joinRequested"
          ></QuizItem>
          <div
            class="pb-2 pb-xl-1"
            v-if="!sortedPublicQuizzes || sortedPublicQuizzes.length == 0"
          >
            <span class="text-muted">No ongoing quizzes.</span>
          </div>
        </b-card>
      </div>
      <div class="col-12 col-lg-4 mb-3 mb-lg-0">
        <div class="card">
          <div class="card-header">Upcoming Quizzes</div>
          <div class="card-body">
            <Calendar />
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-4 mb-3 mb-lg-0">
        <b-card>
          <template v-slot:header>Recent Quizzes</template>
          <template v-slot:footer>
            <span class="text-small">
              To see all of your quizzes go to
              <router-link to="dashboard/history">History</router-link>.
            </span>
          </template>
          <QuizHistoryItem
            v-for="(quiz, id) in myQuizzes"
            v-bind:key="id"
            v-bind:quiz="quiz"
          ></QuizHistoryItem>
        </b-card>
      </div>
    </div>
    <b-modal
      id="modal-join"
      v-model="isJoinModalVisible"
      v-bind:title="'Join quiz \'' + selectedQuiz.name + '\''"
      centered
      no-close-on-backdrop
      @ok="joinSelectedQuiz"
    >
      <p class="my-2">
        It seems like you are trying to join a quiz by
        <HostBadge v-bind:hostName="selectedQuiz.host" prefix />&nbsp;with your
        team
        <TeamBadge
          v-if="activeTeam"
          v-bind:teamName="activeTeam.name"
          prefix
        />. If that is what you intended, please press the join button below.
      </p>
      <p class="my-2">
        <span class="text-muted">
          If you just want to follow the scores you can press 'spectate'
          <span class="d-lg-none">
            (
            <b-icon-eye-fill />)
          </span>
          instead.
        </span>
      </p>
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button
          class="mr-auto"
          v-if="teamCount > 1"
          variant="secondary"
          @click="showTeamModal"
          >Team</b-button
        >
        <b-button variant="secondary" @click="cancel()">Cancel</b-button>
        <b-button variant="secondary" @click="spectateClicked">
          <span class="d-none d-lg-inline">Spectate&nbsp;</span>
          <b-icon-eye-fill />
        </b-button>
        <b-button variant="primary" @click="ok()">Join!</b-button>
      </template>
    </b-modal>
    <b-modal
      id="modal-team"
      v-model="isTeamModalVisible"
      v-bind:title="'Choose your active team'"
      centered
      ok-only
      @hide="teamModalHidden"
    >
      <p class="my-2">
        <b-form-select
          v-if="isTeamModalVisible"
          :value="activeTeam"
          autofocus
          @change="teamSelectionChanged"
        >
          <option
            v-for="team in sortedTeamList"
            v-bind:key="team.id"
            v-bind:value="team"
            >{{ team.name }}</option
          >
        </b-form-select>
      </p>
    </b-modal>
    <DialogError v-model="showError">{{ errorMessage }}</DialogError>
    <DialogInfo v-model="showNoTeamInfo">
      <p>
        You are not part of any team yet. You have to create or join one before
        you can enter a quiz!
      </p>
      <p>
        <router-link to="/dashboard/teams">Manage my teams</router-link>
      </p>
    </DialogInfo>
  </div>
</template>

<script>
import HostBadge from "@/components/HostBadge";
import TeamBadge from "@/components/TeamBadge";
import QuizItem from "@/components/QuizItem";
import QuizHistoryItem from "@/components/QuizHistoryItem";
import Calendar from "@/components/Calendar";
import DialogError from "@/components/DialogError";
import DialogInfo from "@/components/DialogInfo";
import { mapGetters } from "vuex";

export default {
  name: "DashboardHome",
  components: {
    HostBadge,
    TeamBadge,
    QuizItem,
    QuizHistoryItem,
    Calendar,
    DialogInfo,
    DialogError
  },
  data() {
    return {
      // recent quiz history for user
      myQuizzes: [],
      // quiz selected when intending to join
      selectedQuiz: {},
      // toggles join modal visibility
      isJoinModalVisible: false,
      // toggles join modal visibility
      isTeamModalVisible: false,
      errorMessage: "",
      showError: false,
      showNoTeamInfo: false
    };
  },
  computed: {
    ...mapGetters({
      sortedPublicQuizzes: "quizStore/sortedPublicQuizzes",
      teamCount: "teamStore/teamCount",
      sortedTeamList: "teamStore/sortedTeamList"
    }),
    activeTeam() {
      return this.$store.state.teamStore.activeTeam;
    }
  },
  created() {
    this.$topquiz.on(this, "checkJoinIntent_success", params => {
      this.selectedQuiz = params[0];
      this.checkTeamAnGo();
      localStorage.removeItem("JoinIntent");
    });

    this.$topquiz.on(this, "getUserTeams_success", () => {
      //checking intent after teams loaded!
      var joinIntent = localStorage.getItem("JoinIntent");
      if (joinIntent) {
        this.$topquiz.sendCommand(
          "checkJoinIntent",
          this.$topquiz.SessionKey,
          joinIntent
        );
      }
    });

    this.$topquiz.on(this, "getQuizzesHistory_success", params => {
      var quizList = params[0];
      if (quizList.length > 5) {
        quizList.length = 5;
      }
      quizList.forEach(quiz => {
        var id = this.myQuizzes.length;
        this.$set(this.myQuizzes, id, quiz);
      });
    });

    this.$topquiz.on(this, "checkTeamInQuiz_success", params => {
      if (!this.selectedQuiz) {
        return;
      }

      var isInQuiz = params[0];
      if (isInQuiz) {
        this.gotoSelectedQuiz();
      } else {
        this.isJoinModalVisible = true;
      }
    });

    this.$topquiz.on(this, "join_success", () => {
      this.gotoSelectedQuiz();
    });

    this.$topquiz.on(this, "join_error", params => {
      this.showError = true;
      this.errorMessage = params[0];
    });

    this.$store.dispatch("quizStore/loadPublicQuizzes", this);
    this.$store.dispatch("teamStore/loadUserTeams", this);
  },
  destroyed() {
    this.$topquiz.unsubscribe(this);
  },
  methods: {
    joinRequested(quizID) {
      this.selectedQuiz = this.$store.state.quizStore.publicQuizzes[quizID];
      this.checkTeamAnGo();
    },
    checkTeamAnGo() {
      if (this.teamCount == 0) {
        this.showNoTeamInfo = true;
        return;
      }

      if (!this.activeTeam) {
        this.showTeamModal();
        return;
      }

      this.$topquiz.sendCommand(
        "checkTeamInQuiz",
        this.$topquiz.SessionKey,
        this.selectedQuiz.id,
        this.activeTeam.id
      );
    },
    gotoSelectedQuiz() {
      this.$router.push("/play/" + this.selectedQuiz.id);
    },
    joinSelectedQuiz() {
      if (!this.activeTeam) {
        alert("No team");
        return;
      }

      this.$topquiz.sendCommand(
        "join",
        this.$topquiz.SessionKey,
        this.selectedQuiz.id,
        this.activeTeam.id
      );
    },
    spectateClicked() {
      this.isJoinModalVisible = false;
      this.$router.push("/spectate/" + this.selectedQuiz.spectate_code);
    },
    showTeamModal() {
      this.isJoinModalVisible = false;
      this.isTeamModalVisible = true;
    },
    teamSelectionChanged(team) {
      this.$store.dispatch("teamStore/setActiveTeam", team.id);
    },
    teamModalHidden() {
      if (this.selectedQuiz && this.activeTeam) {
        this.isJoinModalVisible = true;
      } else if (!this.activeTeam) {
        this.errorMessage = "You cannot join a quiz without an active team...";
        this.showError = true;
      }
    }
  }
};
</script>
